// ABOUTME: Mirrors the server's CAD gate in the browser, so the download menu enables what PIM accepts.
// ABOUTME: Reads the cadPreview verdict of the assignment-state response, never the completeness flag.

/** Why the server refused to resolve a CAD target for this configuration. */
export type TCadPreviewReason =
    | 'CAD_NOT_CONFIGURED'
    | 'NO_CAD_ATTRIBUTES'
    | 'VARSET_TOO_LONG'
    | 'INVALID_CAD_VALUE'
    | 'CONFIGURATION_INCOMPLETE';

/**
 * The `cadPreview` block of an apply-assignments or assignment-state response. Absent from the
 * response altogether when the tenant or the model has no CAD, which is also the one case where a
 * download request answers DOWNLOAD_NOT_CONFIGURED.
 */
export type TCadPreview = { available: true } | { available: false; reason: TCadPreviewReason };

/**
 * Whether the CADENAS entries of the download menu can be offered for the current configuration.
 *
 * The verdict is the server's own: `cadPreview` is resolved by the same gate the download endpoint
 * consults before generating anything, so reading it back is what keeps the menu from disagreeing
 * with PIM. Deriving the answer from `configurationStatus` instead would re-implement one half of
 * that gate and miss the other — notably `cadPreview.allowIncompleteConfiguration`, the tenant
 * setting whose whole purpose is to let an unresolvable model still produce CAD artifacts.
 *
 * VARSET_TOO_LONG is the one refusal that does not carry over: it is the deeplink's own 2024
 * character cap, a property of the viewer URL rather than of the configuration, and an artifact
 * request is not a deeplink. The download path makes the same exception.
 */
export const isCadDownloadAvailable = (cadPreview: TCadPreview | undefined): boolean => {
    if (!cadPreview) {
        return false;
    }
    return cadPreview.available || cadPreview.reason === 'VARSET_TOO_LONG';
};
